<script lang="ts" setup>
// Confirm for the subscriber "revert customization" flow — the inverse of CustomizeWorkflowModal.
// Discarding a fork is destructive (the customer's edits to the fork are abandoned), so the Workflows
// tab opens this first. The parent owns the actual remove call; this is presentation + confirm/cancel.

interface Props {
  show: boolean;
  workflowName?: string;
  pending?: boolean;
  errorMessage?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "confirm"): void;
  (e: "cancel"): void;
}>();

function onBackdrop() {
  if (props.pending) return;
  emit("cancel");
}
</script>

<template>
  <div
    v-if="props.show"
    class="modal fade show d-block"
    tabindex="-1"
    @click="onBackdrop"
  >
    <div class="modal-dialog" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title mb-0">Revert customization</h2>
          <button
            type="button"
            class="btn-close"
            :disabled="props.pending"
            @click="emit('cancel')"
          ></button>
        </div>
        <div class="modal-body">
          <p>
            This will discard your customized copy of
            <strong>{{ props.workflowName }}</strong> and revert to the publisher's published version.
          </p>
          <ul class="small text-muted">
            <li>Your fork's edits to this workflow will be abandoned — this can't be undone.</li>
            <li>You'll resume receiving the publisher's updates for this workflow automatically.</li>
            <li>Your wizard answers / configuration for this project are kept — only the workflow definition reverts.</li>
          </ul>
          <div v-if="props.errorMessage" class="alert alert-danger mt-3 mb-0">
            <i class="bi bi-exclamation-circle me-2"></i>
            Failed to revert.
            <span> {{ props.errorMessage }}</span>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-secondary-cta"
            :disabled="props.pending"
            @click="emit('cancel')"
          >Cancel</button>
          <button
            type="button"
            class="bb-btn-danger"
            :disabled="props.pending"
            @click="emit('confirm')"
          >
            <span
              v-if="props.pending"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            <i v-else class="bi bi-arrow-counterclockwise me-1"></i>
            Revert to published
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
